
import { FormData, TankInsulationType, DraftsType, OpenChimneyType, ShowerheadType } from '@/types/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Info } from "lucide-react";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface OtherDetailsProps {
  formData: FormData;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleSelectChange: (value: string, name: string) => void;
  required?: boolean;
}

export const OtherDetails = ({ 
  formData, 
  handleInputChange, 
  handleSelectChange,
  required = false 
}: OtherDetailsProps) => {
  const tankInsulationOptions: TankInsulationType[] = [
    "0mm",
    "25mm",
    "80mm",
    "can't find it"
  ];

  const draftsOptions: DraftsType[] = [
    "strong",
    "weak",
    "no drafts"
  ];

  const openChimneyOptions: OpenChimneyType[] = [
    "Yes",
    "No"
  ];

  const showerheadOptions: ShowerheadType[] = [
    "Electric/low-flow",
    "Mixer",
    "Power"
  ];

  return (
    <div className="border border-[#E5E7EB] rounded-lg p-6 space-y-4">
      <h3 className="font-semibold text-[#1F2937] flex items-center gap-2">
        <span className="text-[#6B7280]">🏡</span>
        Other Details {required && <span className="text-red-500">*</span>}
      </h3>
      
      <div className="space-y-4 max-w-[calc(100%-0px)]">
        <div className="space-y-2">
          <Label htmlFor="tankInsulation">Water Tank Insulation</Label>
          <div className="flex items-center gap-2">
            <Select
              value={formData.tankInsulation}
              onValueChange={(value) => handleSelectChange(value, 'tankInsulation')}
            >
              <SelectTrigger id="tankInsulation" className="w-full">
                <SelectValue placeholder="Water tank insulation thickness (mm)" />
              </SelectTrigger>
              <SelectContent>
                {tankInsulationOptions.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="h-4 w-4 text-gray-500" />
                </TooltipTrigger>
                <TooltipContent className="max-w-[300px] text-wrap">
                  <p>To find your water tank, typically look in your loft or attic space, as most older homes store their cold water in a tank located there; if you have a newer property with a mains pressure system, you likely won't have a visible water tank, but your hot water cylinder will usually be found in an airing cupboard near the bathroom.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="drafts">Household Drafts</Label>
          <div className="flex items-center gap-2">
            <Select
              value={formData.drafts}
              onValueChange={(value) => handleSelectChange(value, 'drafts')}
            >
              <SelectTrigger id="drafts" className="w-full">
                <SelectValue placeholder="Household drafts" />
              </SelectTrigger>
              <SelectContent>
                {draftsOptions.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="h-4 w-4 text-gray-500" />
                </TooltipTrigger>
                <TooltipContent className="max-w-[300px] text-wrap">
                  <p>To check for draughts in your home, you can: feel for cold air around doors and windows with your hand, listen for whistling sounds, particularly during windy weather, watch for curtain movement, use a lit candle to see if the flame flickers near potential draft areas, or shine a flashlight around window and door frames to check for light gaps; if you see any of these signs, you likely have draughts.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="openChimney">Open Chimney</Label>
          <Select
            value={formData.openChimney}
            onValueChange={(value) => handleSelectChange(value, 'openChimney')}
          >
            <SelectTrigger id="openChimney" className="w-full">
              <SelectValue placeholder="Open Chimney" />
            </SelectTrigger>
            <SelectContent>
              {openChimneyOptions.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="existingWaterHardware">Pre-existing Low-flow Water Hardware</Label>
          <Input
            id="existingWaterHardware"
            name="existingWaterHardware"
            value={formData.existingWaterHardware}
            onChange={handleInputChange}
            placeholder="Pre-existing low-flow water hardware (if so please provide details)"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="showerheadType">Showerhead Type</Label>
          <Select
            value={formData.showerheadType}
            onValueChange={(value) => handleSelectChange(value, 'showerheadType')}
          >
            <SelectTrigger id="showerheadType" className="w-full">
              <SelectValue placeholder="What type of showerhead do you have?" />
            </SelectTrigger>
            <SelectContent>
              {showerheadOptions.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};
